import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart2Icon, CalendarIcon, FolderIcon, LineChartIcon, ThumbsUpIcon } from "lucide-react";
import caracteristicas from "@/assets/landing/landing-caracteristicas.webp";
import logo from "@/assets/landing/logo.png";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/(marketing)/")({
	component: App,
});

function App() {
	return (
		<main className="flex-1">
			{/* Hero Section */}
			<section className="w-full bg-primary py-4 md:py-6">
				<div className="container mx-auto flex flex-col items-center justify-between gap-8 px-4 md:flex-row md:px-6">
					<img
						alt="Hero"
						className="order-1 mx-auto aspect-square size-137.5 w-full overflow-hidden rounded-xl object-cover sm:w-auto md:order-2"
						height={550}
						loading="eager"
						src={logo}
						width={550}
					/>

					<div className="order-2 max-w-xl space-y-6 text-center md:order-1 md:text-left">
						<h1 className="font-bold text-4xl text-primary-foreground tracking-tighter sm:text-5xl md:text-6xl">
							Gestiona a tus pacientes con facilidad
						</h1>
						<p className="text-lg text-primary-foreground">
							Nuestra aplicación de gestión de pacientes te ayuda a agilizar tu práctica, desde la programación de citas
							hasta la organización de historias clínicas.
						</p>
						<div className="flex flex-col justify-center gap-4 sm:flex-row md:justify-start">
							<Link className={buttonVariants()} to="/auth/login">
								Empezar
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Sección de Beneficios Clave */}
			<section className="w-full bg-background py-12 md:py-24 lg:py-32" id="beneficios">
				<div className="container mx-auto px-4 md:px-6">
					<div className="flex flex-col items-center justify-center space-y-6 text-center">
						<div className="space-y-4">
							<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Por qué elegir nuestra aplicación</h2>
							<p className="max-w-225 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
								Simplifica tus procesos, mejora la atención al paciente y haz crecer tu práctica con nuestras
								herramientas innovadoras.
							</p>
						</div>
					</div>
					<div className="mt-12 grid gap-12 md:grid-cols-2 lg:grid-cols-3">
						<div className="flex flex-col items-center space-y-4 text-center">
							<ThumbsUpIcon className="h-12 w-12 text-primary" />
							<h3 className="font-semibold text-xl">Eficiencia Mejorada</h3>
							<p className="text-muted-foreground">
								Automatiza tareas administrativas para enfocarte en lo que importa: tus pacientes.
							</p>
						</div>
						<div className="flex flex-col items-center space-y-4 text-center">
							<FolderIcon className="h-12 w-12 text-primary" />
							<h3 className="font-semibold text-xl">Datos Seguros</h3>
							<p className="text-muted-foreground">
								Protege la información de tus pacientes con estándares de seguridad de primer nivel.
							</p>
						</div>
						<div className="flex flex-col items-center space-y-4 text-center">
							<LineChartIcon className="h-12 w-12 text-primary" />
							<h3 className="font-semibold text-xl">Crecimiento Constante</h3>
							<p className="text-muted-foreground">
								Utiliza análisis avanzados para tomar decisiones informadas y hacer crecer tu práctica.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Sección de Características */}
			<section className="w-full bg-muted py-12 md:py-24 lg:py-32" id="caracteristicas">
				<div className="container mx-auto px-4 md:px-6">
					<div className="flex flex-col items-center justify-center space-y-6 text-center">
						<div className="space-y-4">
							<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Características Destacadas</h2>
							<p className="max-w-225 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
								Descubre cómo nuestras funcionalidades pueden transformar tu práctica.
							</p>
						</div>
					</div>
					<div className="mt-12 grid items-center gap-12 lg:grid-cols-2 lg:gap-24">
						<div className="flex flex-col space-y-8">
							{/* Lista de Características */}
							<div className="flex items-start space-x-4">
								<CalendarIcon className="h-6 w-6 text-primary" />
								<div>
									<h3 className="font-semibold text-xl">Programación de Citas</h3>
									<p className="mt-2 text-muted-foreground">
										Gestiona las citas con facilidad y reduce ausencias con recordatorios automatizados.
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-4">
								<FolderIcon className="h-6 w-6 text-primary" />
								<div>
									<h3 className="font-semibold text-xl">Historias Clínicas</h3>
									<p className="mt-2 text-muted-foreground">
										Accede rápidamente a la información de tus pacientes y mejora la calidad de la atención.
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-4">
								<BarChart2Icon className="h-6 w-6 text-primary" />
								<div>
									<h3 className="font-semibold text-xl">Análisis y Reportes</h3>
									<p className="mt-2 text-muted-foreground">
										Obtén insights valiosos sobre tu práctica para tomar decisiones estratégicas.
									</p>
								</div>
							</div>
						</div>
						<img
							alt="Características"
							className="rounded-xl shadow-lg"
							height={650}
							src={caracteristicas}
							width={550}
						/>
					</div>
				</div>
			</section>

			{/* Sección de Video Demo */}
			<section className="w-full bg-background py-12 md:py-24 lg:py-32" id="video-demo">
				<div className="container mx-auto px-4 text-center md:px-6">
					<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Mira cómo funciona</h2>
					<p className="mt-4 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
						Aprende en minutos cómo nuestra aplicación puede transformar tu práctica.
					</p>
					<div className="mt-12">
						{/* Placeholder para el Video */}
						<div className="relative h-0 w-full" style={{ paddingBottom: "56.25%" }}>
							<iframe
								allowFullScreen
								className="absolute top-0 left-0 h-full w-full"
								src="https://www.youtube.com/embed/dQw4w9WgXcQ"
								title="Video Demo"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Sección de Testimonios */}
			<section className="w-full bg-muted py-12 md:py-24 lg:py-32" id="testimonios">
				<div className="container mx-auto px-4 text-center md:px-6">
					<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Lo que dicen nuestros clientes</h2>
					<p className="mt-4 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
						Profesionales de la salud confían en nosotros para mejorar sus prácticas.
					</p>
					<div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
						<div className="rounded-lg bg-background p-6 shadow-md">
							<p className="text-left text-muted-foreground">
								&quot;Esta aplicación ha revolucionado la forma en que gestiono mi consultorio. ¡Altamente
								recomendada!&quot;
							</p>
							<div className="mt-4 flex items-center space-x-3">
								<img
									alt="Dr. Juan Pérez"
									className="rounded-full"
									height={40}
									src="https://via.placeholder.com/40"
									width={40}
								/>
								<div className="flex flex-col items-start">
									<h4 className="font-semibold">Dr. Juan Pérez</h4>
									<p className="text-muted-foreground text-sm">Odontólogo</p>
								</div>
							</div>
						</div>
						<div className="rounded-lg bg-background p-6 shadow-md">
							<p className="text-left text-muted-foreground">
								&quot;Esta aplicación ha revolucionado la forma en que gestiono mi consultorio. ¡Altamente
								recomendada!&quot;
							</p>
							<div className="mt-4 flex items-center space-x-3">
								<img
									alt="Dr. Juan Pérez"
									className="rounded-full"
									height={40}
									src="https://via.placeholder.com/40"
									width={40}
								/>
								<div className="flex flex-col items-start">
									<h4 className="font-semibold">Dr. Juan Pérez</h4>
									<p className="text-muted-foreground text-sm">Odontólogo</p>
								</div>
							</div>
						</div>
						<div className="rounded-lg bg-background p-6 shadow-md">
							<p className="text-left text-muted-foreground">
								&quot;Esta aplicación ha revolucionado la forma en que gestiono mi consultorio. ¡Altamente
								recomendada!&quot;
							</p>
							<div className="mt-4 flex items-center space-x-3">
								<img
									alt="Dr. Juan Pérez"
									className="rounded-full"
									height={40}
									src="https://via.placeholder.com/40"
									width={40}
								/>
								<div className="flex flex-col items-start">
									<h4 className="font-semibold">Dr. Juan Pérez</h4>
									<p className="text-muted-foreground text-sm">Odontólogo</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Sección de Precios */}
			<section className="w-full bg-background py-12 md:py-24 lg:py-32" id="precios">
				<div className="container mx-auto px-4 md:px-6">
					<div className="flex flex-col items-center justify-center space-y-6 text-center">
						<div className="space-y-4">
							<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Planes flexibles para tu práctica</h2>
							<p className="max-w-225 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
								Elige el plan que mejor se adapte a tus necesidades. ¡30 días de prueba gratis en todos los planes!
							</p>
						</div>
					</div>
					<div className="mt-12 grid gap-8 lg:grid-cols-3">
						{/* Plan Starter */}
						<Card className="flex flex-col justify-between bg-background p-8 shadow-md">
							<div>
								<CardHeader>
									<CardTitle className="font-bold text-2xl">Starter</CardTitle>
									<CardDescription className="text-lg">Ideal para dentistas que comienzan</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-6">
										<div className="flex flex-col items-center justify-center gap-1">
											<div className="flex items-baseline gap-2">
												<span className="font-bold text-5xl text-primary">$49</span>
												<span className="text-lg text-muted-foreground">/mes</span>
											</div>
											<div className="text-muted-foreground text-sm">o $39/mes facturado anualmente</div>
										</div>
										<ul className="space-y-3 text-left text-muted-foreground text-sm">
											<li>✓ 1 sucursal</li>
											<li>✓ 1 doctor</li>
											<li>✓ Hasta 100 pacientes</li>
											<li>✓ 500 MB de almacenamiento</li>
											<li>✓ 30 citas por mes</li>
											<li>✓ Odontograma básico</li>
											<li>✓ 5 tipos de procedimientos</li>
											<li>✓ 3 formularios personalizados</li>
											<li>✓ Dashboard básico</li>
											<li>✓ Soporte por email (48-72h)</li>
										</ul>
									</div>
								</CardContent>
							</div>
							<CardFooter>
								<Link className="w-full" to="/auth/register">
									<Button className="w-full">Probar 30 días gratis</Button>
								</Link>
							</CardFooter>
						</Card>

						{/* Plan Profesional - Más Popular */}
						<Card className="relative flex flex-col justify-between border-2 border-primary bg-background p-8 shadow-md">
							<div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
								<div className="rounded-full bg-primary px-4 py-1 font-semibold text-primary-foreground text-sm">
									Más Popular
								</div>
							</div>
							<div>
								<CardHeader>
									<CardTitle className="font-bold text-2xl">Profesional</CardTitle>
									<CardDescription className="text-lg">Para consultorios establecidos</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-6">
										<div className="flex flex-col items-center justify-center gap-1">
											<div className="flex items-baseline gap-2">
												<span className="font-bold text-5xl text-primary">$129</span>
												<span className="text-lg text-muted-foreground">/mes</span>
											</div>
											<div className="text-muted-foreground text-sm">o $99/mes facturado anualmente</div>
										</div>
										<ul className="space-y-3 text-left text-muted-foreground text-sm">
											<li>✓ Hasta 5 sucursales</li>
											<li>✓ Hasta 10 doctores</li>
											<li>✓ Hasta 500 pacientes</li>
											<li>✓ 10 GB de almacenamiento</li>
											<li>✓ Citas ilimitadas</li>
											<li>✓ Odontograma completo</li>
											<li>✓ 20 tipos de procedimientos</li>
											<li>✓ 15 formularios personalizados</li>
											<li>✓ Dashboard completo + KPIs</li>
											<li>✓ Análisis de tendencias</li>
											<li>✓ Facturación avanzada</li>
											<li>✓ Exportación de datos (PDF/CSV)</li>
											<li>✓ Soporte prioritario (24h)</li>
										</ul>
									</div>
								</CardContent>
							</div>
							<CardFooter>
								<Link className="w-full" to="/auth/register">
									<Button className="w-full">Probar 30 días gratis</Button>
								</Link>
							</CardFooter>
						</Card>

						{/* Plan Empresarial */}
						<Card className="flex flex-col justify-between bg-background p-8 shadow-md">
							<div>
								<CardHeader>
									<CardTitle className="font-bold text-2xl">Empresarial</CardTitle>
									<CardDescription className="text-lg">Para cadenas de consultorios</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-6">
										<div className="flex flex-col items-center justify-center gap-1">
											<div className="flex items-baseline gap-2">
												<span className="font-bold text-5xl text-primary">$299</span>
												<span className="text-lg text-muted-foreground">/mes</span>
											</div>
											<div className="text-muted-foreground text-sm">o $249/mes facturado anualmente</div>
										</div>
										<ul className="space-y-3 text-left text-muted-foreground text-sm">
											<li>✓ Sucursales ilimitadas</li>
											<li>✓ Doctores ilimitados</li>
											<li>✓ Pacientes ilimitados</li>
											<li>✓ 50 GB de almacenamiento</li>
											<li>✓ Citas ilimitadas</li>
											<li>✓ Todas las funciones desbloqueadas</li>
											<li>✓ Analytics personalizados</li>
											<li>✓ Soporte dedicado (2-4h)</li>
											<li>✓ Gestor de cuenta</li>
											<li>✓ Capacitación personalizada</li>
											<li>✓ Dashboard completo + KPIs</li>
											<li>✓ Análisis de tendencias</li>
											<li>✓ Facturación avanzada</li>
											<li>✓ Exportación de datos (PDF/CSV)</li>
										</ul>
									</div>
								</CardContent>
							</div>
							<CardFooter>
								<Link className="w-full" to="/auth/register">
									<Button className="w-full">Probar 30 días gratis</Button>
								</Link>
							</CardFooter>
						</Card>
					</div>

					{/* Plan Personalizado */}
					<div className="mx-auto mt-16 max-w-5xl">
						<div className="rounded-lg bg-background p-8 shadow-md">
							<div className="flex flex-col items-center justify-between gap-6 md:flex-row">
								<div className="space-y-4">
									<h3 className="font-bold text-2xl text-primary">¿Necesitas un plan a medida?</h3>
									<p className="text-muted-foreground">
										Nuestro equipo puede ayudarte a encontrar la solución perfecta para tu práctica.
									</p>
								</div>
								<a
									className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 font-medium text-base text-primary-foreground shadow transition-colors hover:bg-primary-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-primary disabled:pointer-events-none disabled:opacity-50"
									href="#contacto"
								>
									Contactar a Ventas
								</a>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Sección de Preguntas Frecuentes */}
			<section className="w-full bg-muted py-12 md:py-24 lg:py-32" id="faq">
				<div className="container mx-auto px-4 md:px-6">
					<div className="text-center">
						<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">Preguntas Frecuentes</h2>
						<p className="mt-4 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
							Aquí tienes respuestas a algunas de las preguntas más comunes.
						</p>
					</div>
					<div className="mx-auto mt-12 max-w-3xl text-background">
						<Accordion collapsible type="single">
							<AccordionItem className="rounded bg-white p-3" value="item-1">
								<AccordionTrigger className="font-semibold text-xl">
									¿Cómo puedo restablecer mi contraseña?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground">
									Puedes restablecer tu contraseña haciendo clic en &quot;Olvidé mi contraseña&quot; en la página de
									inicio de sesión.
								</AccordionContent>
							</AccordionItem>
							<AccordionItem className="mt-2 rounded bg-white p-3" value="item-2">
								<AccordionTrigger className="font-semibold text-xl">¿Cómo contacto al soporte?</AccordionTrigger>
								<AccordionContent className="text-muted-foreground">
									Para contactar al soporte, completa el formulario en la sección de contacto y te responderemos lo
									antes posible.
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				</div>
			</section>

			{/* Sección de Contacto */}
			<section className="w-full bg-background py-12 md:py-24 lg:py-32" id="contacto">
				<div className="container mx-auto px-4 md:px-6">
					<div className="flex flex-col items-center justify-center space-y-6 text-center">
						<div className="space-y-4">
							<h2 className="font-bold text-3xl tracking-tighter sm:text-5xl">¿Cómo podemos ayudarte?</h2>
							<p className="max-w-225 text-muted-foreground md:text-xl lg:text-base xl:text-xl">
								Completa el siguiente formulario y nos comunicaremos contigo a la brevedad.
							</p>
						</div>
					</div>
					<div className="mx-auto mt-12 w-full max-w-md">
						<form className="space-y-6">
							<div>
								<Label htmlFor="name">Nombre</Label>
								<Input className="mt-1" id="name" placeholder="John Doe" />
							</div>
							<div>
								<Label htmlFor="email">Correo Electrónico</Label>
								<Input className="mt-1" id="email" placeholder="john@example.com" type="email" />
							</div>
							<div>
								<Label htmlFor="message">Mensaje</Label>
								<Textarea className="mt-1" id="message" placeholder="¿Cómo podemos ayudarte?" rows={4} />
							</div>
							<Button className="w-full" type="submit">
								Enviar
							</Button>
						</form>
						<div className="mt-8 text-center text-muted-foreground">
							<p>También puedes contactarnos a través de:</p>
							<p>
								Email:{" "}
								<a className="text-primary" href="mailto:contacto@tuempresa.com">
									contacto@tuempresa.com
								</a>
							</p>
							<p>
								Teléfono:{" "}
								<a className="text-primary" href="tel:+1234567890">
									+1 234 567 890
								</a>
							</p>
						</div>
					</div>
				</div>
			</section>
		</main>
	);
}
